import {
  ChatStatus,
  RecordingStatus,
  RESEARCH_CHAT_MODE,
  ULTRA_CHAT_MODE,
  useStores,
} from '@littlebird/sdk';
import mergeRefs from 'merge-refs';
import { observer } from 'mobx-react-lite';
import {
  animate,
  AnimatePresence,
  type AnimationPlaybackControls,
  motion,
  useReducedMotion,
} from 'motion/react';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  type MutableRefObject,
} from 'react';

import { Icons } from '@/assets/icons/icons';
import { IconButton, Tap, XStack, YStack } from '@/components/ui';
import {
  useCurrentChatMode,
  useChatModeLimitAutoSwitch,
} from '@/features/chat/chat.hooks';
import { chatCostLiveStore } from '@/features/chat/cost-footer/chat-cost-live.store';
import { ComposerCostFooter } from '@/features/chat/cost-footer/ComposerCostFooter';
import { getVoiceModeMediaStream } from '@/features/chat/voice/voice-mode-desktop-platform';
import { HivemindPicker } from '@/features/hivemind';
import { TaggedPeopleCards } from '@/features/people-wiki/connections';
import { useShortcutKeys } from '@/features/shortcuts';
import { getDictationUnsupportedReason } from '@/features/speech-input/capture-controller';
import {
  useSubscription,
  ResearchLimitWarning,
  LiteModeBanner,
  OutOfCreditsCollapsedComposer,
  OutOfCreditsComposerBanner,
  UsageWarningNotification,
} from '@/features/subscription';
import { TeamChatComposerHint } from '@/features/team-chats/team-chat-composer-hint';
import { TeamChatDraftButton } from '@/features/team-chats/team-chat-draft-button';
import { TeamChatDraftCard } from '@/features/team-chats/team-chat-draft-card';
import {
  useTeamChatComposer,
  type TeamChatComposerConfig,
  type TeamChatComposerState,
} from '@/features/team-chats/use-team-chat-composer';
import { useHashRoute } from '@/hooks/useHashRoute';
import { useElementSize } from '@/hooks/useResizeObserver';
import { useScrollFade } from '@/hooks/useScrollFade';
import { useScrollToBottomOnGrow } from '@/hooks/useScrollToBottomOnGrow';
import { settingsStore } from '@/stores/settings.store';
import { userStore } from '@/stores/user.store';
import { trackEvent } from '@/utils/analytics';
import { logChatRenderDebug } from '@/utils/chat-render-debug';
import { classList } from '@/utils/cn';
import { NEW_CHAT_ID } from '@/utils/constants';

import { RegenerateButton } from '../Chat/RegenerateButton';
import { FileGallery } from '../FileGallery';

import { ChatEngineBadge } from './chat-engine-badge';
import { ChatModeDropdown } from './ChatModeButtons';
import { ComposerAddMenu } from './ComposerAddMenu';
import { ComposerEditor } from './ComposerEditor';
import { ComposerProjectDropdown } from './ComposerProjectDropdown';
import { LegacyChatWarning } from './LegacyChatWarning';
import { MicrophoneButton } from './MicrophoneButton';
import { QueuedMessages } from './QueuedMessages';
import { SendButton } from './SendButton';
import { type ComposerCollapsed, useComposer } from './useComposer';
import { useFocusComposerOnType } from './useFocusComposerOnType';
import { modEnterSends } from './useRegisterSendChat';
import { VoiceModeButton } from './VoiceModeButton';
import { VoiceModeWaveform } from './VoiceModeWaveform';

export type { ComposerCollapsed } from './useComposer';

interface ComposerProps {
  threadId: string;
  setIsDraggingFile: (isDraggingFile: boolean) => void;
  isNewThread?: boolean;
  onThreadCreated?: (threadId: string) => void;
  onOpenThread?: (threadId: string) => void;
  projectId?: string | null;
  hideProjectSelector?: boolean;
  collapsed?: ComposerCollapsed;
  frameless?: boolean;
  placeholder?: string;
  dock?: boolean;
  hideCostFooter?: boolean;
  hideVoiceInput?: boolean;
  focusOnType?: boolean;
  autoFocus?: boolean;
  trailingAction?: React.ReactNode;
  sendAskUserAnswersRef?: MutableRefObject<() => void>;
  /** A shared project's chat: Send may post as the viewer, and their Littlebird's draft shows in the box */
  teamChat?: TeamChatComposerConfig;
}

export const Composer = observer((props: ComposerProps) => {
  const { drafts: draftsStore, messages: messagesStore } = useStores();
  const { currentChatMode, setCurrentChatMode } = useCurrentChatMode();
  const { setHash } = useHashRoute();
  const shouldReduceMotion = useReducedMotion();
  const isCollapsed = Boolean(props.collapsed);
  const collapsedState =
    props.collapsed === undefined ? undefined : isCollapsed;
  const collapsedConfig =
    typeof props.collapsed === 'object' ? props.collapsed : undefined;
  useChatModeLimitAutoSwitch();

  const micSlotRef = useRef<HTMLDivElement>(null);
  const prevCollapsedRef = useRef(collapsedState);

  useLayoutEffect(() => {
    const prev = prevCollapsedRef.current;
    prevCollapsedRef.current = collapsedState;
    if (
      prev === collapsedState ||
      prev === undefined ||
      collapsedState === undefined ||
      shouldReduceMotion
    ) {
      return;
    }
    const el = micSlotRef.current;
    if (!el) return;
    el.style.opacity = '0';
    let raf2 = 0;
    let controls: AnimationPlaybackControls | undefined;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        controls = animate(
          el,
          { opacity: 1, y: [4, 0], filter: ['blur(2px)', 'blur(0px)'] },
          { duration: 0.26, ease: 'easeOut', delay: 0.05 }
        );
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      controls?.stop();
      el.style.opacity = '';
      el.style.transform = '';
      el.style.filter = '';
    };
  }, [collapsedState, shouldReduceMotion]);

  const teamSubmitRef = useRef<TeamChatComposerState['submit'] | undefined>(
    undefined
  );
  const {
    thread,
    threadId,
    draftId,
    resendQuery,
    draft,
    inputRef,
    composerEditorHandleRef,
    mentionsRef,
    blurInputHotkeyRef,
    setDraftContent,
    handlePaste,
    handleFileSelection,
    handleSavePastedText,
    handleInsertPastedText,
    isBusy,
    isBlockedByCredits,
    isComposerEmpty,
    recordingStatus,
    sendMessage,
    sendStopChatHotkeyRef,
    startRecording,
    stopRecording,
    cancelRecording,
    canUseVoiceMode,
    isVoiceModeActive,
    toggleVoiceMode,
    dictationMediaStreamRef,
    handleStopChat,
    isSendDisabled,
    canAttachFile,
    attachmentsCount,
    selectedProject,
    projectOptions,
    handleSelectProject,
    handleCreateProject,
    canQueueMessages,
    sendNowAction,
    handleSendNow,
    handleRemoveQueuedMessage,
    isSendingQueuedMessageNow,
  } = useComposer({
    ...props,
    onSubmitOverride: props.teamChat
      ? (text, mentions, hasFiles) =>
          teamSubmitRef.current?.(text, mentions, hasFiles) ??
          Promise.resolve('ask')
      : undefined,
  });
  const teamChat = useTeamChatComposer(
    props.teamChat,
    draftId,
    mentionsRef.current
  );
  teamSubmitRef.current = teamChat?.submit;
  const isTeamMember = teamChat !== null && !props.teamChat?.viewerIsOwner;
  logChatRenderDebug(
    'composer-render',
    {
      threadStatus: thread?.status,
      draftLength: draft?.content.length ?? 0,
    },
    threadId
  );
  const composerBlocked = isBlockedByCredits;
  const editorDisabled =
    isBlockedByCredits ||
    (!isVoiceModeActive && recordingStatus !== RecordingStatus.None);
  useFocusComposerOnType(
    inputRef,
    Boolean(props.focusOnType) && !isCollapsed && !editorDisabled
  );
  const { sendAskUserAnswersRef } = props;
  useLayoutEffect(() => {
    if (sendAskUserAnswersRef) sendAskUserAnswersRef.current = sendMessage;
  }, [sendAskUserAnswersRef, sendMessage]);

  // Only this input's own dictation is stopped here. The Voice Mode session is stopped by
  // `watchSpeechLifecycle`, which keeps running when no chat page is open
  useEffect(() => {
    if (!composerBlocked) return;
    cancelRecording();
  }, [cancelRecording, composerBlocked]);

  const { ref: composerRef, width: composerWidth } =
    useElementSize<HTMLDivElement>();
  // Dictation expands to four controls; Voice Mode keeps its compact waveform
  const hideModeForDictation =
    !isVoiceModeActive &&
    recordingStatus === RecordingStatus.Recording &&
    composerWidth > 0 &&
    composerWidth < 500;
  // On a touch-primary viewport Enter inserts the composer's newline, so the
  // button is the sender and the tooltip shows the hardware shortcut
  const sendTooltipHotkey = useShortcutKeys(
    'chat.send',
    modEnterSends() ? (['mod', 'enter'] as const) : (['enter'] as const)
  );

  const isInternalUser = userStore.isInternalUser;
  const costFooterEnabled = isInternalUser && settingsStore.costFooterEnabled;
  // A browser that cannot record audio hides dictation and Voice Mode; the
  // reason goes to the dev log once (see getDictationUnsupportedReason)
  const dictationUnsupportedReason = getDictationUnsupportedReason();
  const showVoiceControls =
    !props.hideVoiceInput && !dictationUnsupportedReason;
  const chatHasMessages =
    costFooterEnabled &&
    messagesStore.getMessagesByThreadId(props.threadId).length > 0;
  useEffect(() => {
    if (!costFooterEnabled) {
      chatCostLiveStore.setActiveChatId(null);
      return;
    }
    const chatId = props.threadId === NEW_CHAT_ID ? null : props.threadId;
    chatCostLiveStore.setActiveChatId(chatId);
    // A closed composer's chat stops loading costs on each reconnect. Another
    // composer may have made its own chat the active one meanwhile
    return () => {
      if (chatCostLiveStore.activeChatId === chatId)
        chatCostLiveStore.setActiveChatId(null);
    };
  }, [props.threadId, costFooterEnabled]);

  const liveAskUserRequest =
    props.threadId === NEW_CHAT_ID
      ? null
      : messagesStore.getLiveAskUserRequest(props.threadId);
  const composerPlaceholder = liveAskUserRequest
    ? 'Add a note — answers send with your message'
    : props.placeholder;

  const { refetch: refetchSubscription } = useSubscription();

  const mergedInputRef = useMemo(
    () => mergeRefs(inputRef, blurInputHotkeyRef, sendStopChatHotkeyRef),
    [inputRef, blurInputHotkeyRef, sendStopChatHotkeyRef]
  ) as (element: HTMLElement | null) => void;

  const prevIsBusyRef = useRef(isBusy);
  const prevThreadStatusRef = useRef(thread?.status);
  const messageId = thread?.lastMessageId;

  useEffect(() => {
    if (prevIsBusyRef.current === true && isBusy === false) {
      const chatId = threadId;
      trackEvent('MESSAGE_COMPLETED', {
        message_id: messageId,
        chat_id: chatId,
      });
    }
    prevIsBusyRef.current = isBusy;
  }, [isBusy, messageId, threadId]);

  const selectedTexts = draft?.selectedTexts ?? [];
  const hasFiles = (draft?.files?.length ?? 0) > 0;
  const quotesScrollRef = useRef<HTMLDivElement>(null);
  const { maskStyle: quotesMaskStyle, suppressBottomFade } = useScrollFade(
    quotesScrollRef,
    { enabled: selectedTexts.length > 0 }
  );

  const suppressFadeDuringScroll = useCallback(
    () => suppressBottomFade(500),
    [suppressBottomFade]
  );
  useScrollToBottomOnGrow(
    quotesScrollRef,
    selectedTexts.length,
    suppressFadeDuringScroll
  );

  useEffect(() => {
    if (selectedTexts.length > 0 || hasFiles) inputRef.current?.focus();
  }, [selectedTexts.length, hasFiles, inputRef]);

  // Ultra shares Max's daily quota, so it reuses the research limit warning
  const isResearchMode =
    currentChatMode === RESEARCH_CHAT_MODE ||
    currentChatMode === ULTRA_CHAT_MODE ||
    userStore.user?.chat_mode_details?.[currentChatMode]?.response_mode ===
      'research';

  useEffect(() => {
    const currentStatus = thread?.status;
    if (
      currentStatus === ChatStatus.Streaming &&
      prevThreadStatusRef.current !== ChatStatus.Streaming
    ) {
      refetchSubscription();
    }
    prevThreadStatusRef.current = currentStatus;
  }, [thread?.status, refetchSubscription]);

  if (thread?.status === ChatStatus.Errored) {
    return (
      <RegenerateButton onPress={resendQuery} error={thread.errorMessage} />
    );
  }

  if (isCollapsed && isBlockedByCredits) {
    return <OutOfCreditsCollapsedComposer />;
  }

  const hasCancelableActivity =
    isBusy || recordingStatus !== RecordingStatus.None;

  const sendSlotVoiceMode =
    showVoiceControls &&
    canUseVoiceMode &&
    !isVoiceModeActive &&
    isComposerEmpty
      ? {
          onStart: toggleVoiceMode,
          disabled:
            isBlockedByCredits ||
            recordingStatus === RecordingStatus.Processing,
        }
      : undefined;

  const controlsPresence = {
    initial: shouldReduceMotion
      ? { opacity: 0 }
      : { opacity: 0, y: 4, filter: 'blur(2px)' },
    animate: {
      opacity: 1,
      y: 0,
      filter: 'blur(0px)',
      transition: shouldReduceMotion
        ? { duration: 0 }
        : { duration: 0.26, ease: 'easeOut' as const, delay: 0.05 },
    },
    exit: {
      opacity: 0,
      filter: 'blur(2px)',
      transition: shouldReduceMotion
        ? { duration: 0 }
        : { duration: 0.12, ease: 'easeOut' as const },
    },
  };

  return (
    <>
      {thread?.isLegacyChat && <LegacyChatWarning />}
      {!isCollapsed && (
        <>
          <OutOfCreditsComposerBanner />
          <LiteModeBanner />
          <TaggedPeopleCards key={draftId} draftId={draftId} />
        </>
      )}
      <div
        ref={composerRef}
        data-composer-collapsed={isCollapsed ? '' : undefined}
        className={classList(
          'rounded-2xl',
          'smoothing-default',
          'cursor-[text]',
          'relative z-10',
          isCollapsed
            ? classList(
                'flex items-center gap-1 overflow-hidden px-2.5 transition-colors',
                !collapsedConfig && 'h-full hover:bg-surface-hover',
                collapsedConfig?.collapsedVariant === 'card' &&
                  'border border-border-strong bg-surface shadow-sm hover:bg-surface-hover',
                collapsedConfig?.collapsedVariant === 'translucent' &&
                  'glass-theme composer-translucent border shadow-sm'
              )
            : props.frameless
              ? 'p-2.5'
              : 'border border-border-strong shadow-sm bg-surface p-2.5'
        )}
        style={
          collapsedConfig
            ? { height: `${collapsedConfig.collapsedHeight}px` }
            : undefined
        }
        onClick={(e) => {
          if ((e.target as HTMLElement).closest('button')) return;
          inputRef.current?.focus();
        }}
      >
        {!isCollapsed && (
          <YStack>
            <AnimatePresence>
              {isResearchMode && (
                <ResearchLimitWarning
                  isNewThread={props.isNewThread}
                  className="-mx-2.5 -mt-2.5 mb-3"
                />
              )}
              <UsageWarningNotification className="-mx-2.5 -mt-2.5 mb-3" />
            </AnimatePresence>
            {selectedTexts.length > 0 && (
              <div
                ref={quotesScrollRef}
                className={classList(
                  'mb-2 flex flex-col gap-2',
                  selectedTexts.length > 1 &&
                    'max-h-48 overflow-y-auto scrollbar-hide'
                )}
                style={quotesMaskStyle}
              >
                {selectedTexts.map((selection) => (
                  <motion.div
                    key={selection.id}
                    layout
                    transition={{
                      type: 'spring',
                      stiffness: 500,
                      damping: 40,
                      mass: 0.6,
                    }}
                    className={classList(
                      'flex items-start gap-2 shrink-0',
                      'bg-surface-active rounded-xl p-3',
                      'smoothing-default cursor-default overflow-hidden'
                    )}
                  >
                    <YStack className="flex-1 gap-1">
                      <span className="text-xs text-foreground-subtle">
                        Selected text
                      </span>
                      <span className="text-sm text-foreground leading-relaxed line-clamp-2">
                        {selection.text}
                      </span>
                    </YStack>
                    <Tap>
                      <IconButton
                        size="xs"
                        variant="ghost"
                        icon={Icons.cancel}
                        type="button"
                        onClick={() =>
                          draftsStore.removeSelectedText(draftId, selection.id)
                        }
                      />
                    </Tap>
                  </motion.div>
                ))}
              </div>
            )}
            <AnimatePresence>
              {draft?.files && draft.files.length > 0 && (
                <motion.div
                  layout
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto', filter: 'blur(0px)' }}
                  exit={{ opacity: 0, height: 0, filter: 'blur(10px)' }}
                  transition={{
                    opacity: { duration: 0.2 },
                    height: { type: 'spring', stiffness: 800, damping: 35 },
                  }}
                >
                  <FileGallery
                    attachedFiles={draft.files}
                    removeFile={(file) => draftsStore.removeFile(draftId, file)}
                    onRetryFile={(file) =>
                      draftsStore.retryFileUpload(draftId, file)
                    }
                    onSaveText={handleSavePastedText}
                    onInsertText={handleInsertPastedText}
                    isPreviewEnabled={true}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </YStack>
        )}

        <div
          className={classList(
            'relative',
            isCollapsed ? 'flex h-full flex-1 items-center min-w-0' : 'w-full'
          )}
        >
          {canQueueMessages && (
            <QueuedMessages
              threadId={threadId}
              sendNowAction={sendNowAction}
              isSendingNow={isSendingQueuedMessageNow}
              onSendNow={handleSendNow}
              onRemove={handleRemoveQueuedMessage}
            />
          )}
          <AnimatePresence initial={false}>
            {teamChat?.draft && !teamChat.isEditingDraft && (
              <motion.div key="team-chat-draft" {...controlsPresence}>
                <TeamChatDraftCard
                  draft={teamChat.draft}
                  isSending={teamChat.isPosting}
                  onSend={() => void teamChat.sendDraft()}
                  onEdit={() => {
                    teamChat.acceptDraft();
                    inputRef.current?.focus();
                  }}
                  onDiscard={teamChat.discard}
                />
              </motion.div>
            )}
          </AnimatePresence>
          <ComposerEditor
            inputRef={mergedInputRef}
            editorHandleRef={composerEditorHandleRef}
            value={draft?.content ?? ''}
            document={draft?.draftDocument}
            placeholder={composerPlaceholder}
            onModEnter={
              teamChat?.draft?.text && isComposerEmpty
                ? () => {
                    void teamChat.sendDraft();
                    return true;
                  }
                : undefined
            }
            collapsed={isCollapsed}
            autoFocus={!isCollapsed && props.autoFocus !== false}
            mentionsRef={mentionsRef}
            draftId={draftId}
            onChange={(text, document) => {
              setDraftContent(text, document);
              teamChat?.onComposerText(text);
            }}
            onPaste={handlePaste}
            disabled={editorDisabled}
          />
        </div>

        <XStack
          inert={isBlockedByCredits && !hasCancelableActivity}
          className={classList(
            'items-center',
            isBlockedByCredits && 'opacity-60',
            isCollapsed ? 'shrink-0' : 'ml-0.5'
          )}
        >
          <AnimatePresence
            mode="popLayout"
            initial={false}
            anchorX="right"
            anchorY="bottom"
          >
            {!isCollapsed && (
              <motion.div
                key="composer-controls"
                className="flex flex-1 items-center"
                {...controlsPresence}
              >
                <XStack className="items-center gap-0.5">
                  <ComposerAddMenu
                    draftId={draftId}
                    canAttachFile={canAttachFile}
                    attachmentsCount={attachmentsCount}
                    selectedProject={selectedProject}
                    projectOptions={projectOptions}
                    onAddFiles={handleFileSelection}
                    onSelectProject={handleSelectProject}
                    onCreateProject={handleCreateProject}
                    hideProjectSelector={props.hideProjectSelector}
                    isNewChat={threadId === NEW_CHAT_ID}
                  />
                  {!isTeamMember && <HivemindPicker draftId={draftId} />}
                  {!props.hideProjectSelector && (
                    <ComposerProjectDropdown
                      selectedProject={selectedProject}
                      projectOptions={projectOptions}
                      onSelectProject={handleSelectProject}
                      onCreateProject={handleCreateProject}
                    />
                  )}
                </XStack>
              </motion.div>
            )}
          </AnimatePresence>
          <XStack data-composer-actions className="items-center gap-2">
            <AnimatePresence
              mode="popLayout"
              initial={false}
              anchorX="right"
              anchorY="bottom"
            >
              {!isCollapsed && teamChat && (
                <motion.div
                  key="team-chat-draft"
                  className="flex items-center"
                  {...controlsPresence}
                >
                  <TeamChatDraftButton
                    onDraft={() => {
                      teamChat.askForDraft(draft?.content ?? '');
                      if (draft) draftsStore.removeDraft(draft);
                    }}
                  />
                </motion.div>
              )}
              {!isCollapsed && !hideModeForDictation && !isTeamMember && (
                <motion.div
                  key="chat-mode"
                  className="flex items-center gap-1 -mr-1.5"
                  {...controlsPresence}
                >
                  <ChatEngineBadge
                    chatKey={threadId === NEW_CHAT_ID ? draftId : threadId}
                  />
                  <ChatModeDropdown
                    mode={currentChatMode}
                    setMode={setCurrentChatMode}
                    onRequestUpgrade={() => setHash('settings/Subscription')}
                  />
                </motion.div>
              )}
              {!isCollapsed && props.trailingAction && (
                <motion.div
                  key="trailing-action"
                  className="flex items-center"
                  {...controlsPresence}
                >
                  {props.trailingAction}
                </motion.div>
              )}
            </AnimatePresence>
            <div ref={micSlotRef} className="flex items-center">
              {showVoiceControls &&
                (isVoiceModeActive ? (
                  <VoiceModeWaveform
                    mediaStream={
                      recordingStatus === RecordingStatus.Recording
                        ? getVoiceModeMediaStream()
                        : null
                    }
                    disableLayoutAnimation={props.dock}
                  />
                ) : (
                  <MicrophoneButton
                    mediaStreamRef={dictationMediaStreamRef}
                    recordingStatus={recordingStatus}
                    onStartRecording={startRecording}
                    onStopRecording={stopRecording}
                    onCancelRecording={cancelRecording}
                    disabled={isBusy || isBlockedByCredits}
                    disableLayoutAnimation={props.dock}
                    className={
                      isCollapsed ? 'hover:!bg-surface-active' : undefined
                    }
                  />
                ))}
            </div>
            {showVoiceControls &&
              canUseVoiceMode &&
              (isVoiceModeActive || isCollapsed) && (
                <VoiceModeButton
                  isActive={isVoiceModeActive}
                  onToggle={toggleVoiceMode}
                  className={classList(
                    '-ml-1.5',
                    isCollapsed && 'hover:!bg-surface-active'
                  )}
                  // Starting Voice Mode can replace active dictation, but not transcription
                  disabled={
                    !isVoiceModeActive &&
                    (isBlockedByCredits ||
                      isBusy ||
                      recordingStatus === RecordingStatus.Processing)
                  }
                />
              )}
            <AnimatePresence
              mode="popLayout"
              initial={false}
              anchorX="right"
              anchorY="bottom"
            >
              {!isCollapsed && (
                <motion.div
                  key="send"
                  className="flex items-center ml-0.5"
                  {...controlsPresence}
                >
                  <SendButton
                    isBusy={isBusy}
                    onSend={sendMessage}
                    onStop={handleStopChat}
                    disabled={isSendDisabled}
                    tooltipHotkey={sendTooltipHotkey}
                    voiceMode={sendSlotVoiceMode}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </XStack>
        </XStack>
      </div>
      {!isCollapsed && teamChat && (
        <TeamChatComposerHint hint={teamChat.hint} />
      )}
      {!isCollapsed &&
        !teamChat &&
        !props.hideCostFooter &&
        costFooterEnabled && (
          <ComposerCostFooter
            store={chatCostLiveStore}
            className="mt-1 h-8 shrink-0 px-2"
            reserveSpace={chatHasMessages}
          />
        )}
    </>
  );
});
